import { getTransaction, patchTransaction, deleteTransaction, listCategories, type TransactionDetail, type CategoryListItem } from '../api-client';
import { navigate } from '../router';

export async function renderTransactionDetail(id: string): Promise<void> {
  const root = document.getElementById('app')!;
  root.innerHTML = `<p class="muted" style="padding:16px">Loading…</p>`;

  let tx: TransactionDetail;
  try {
    tx = await getTransaction(id);
  } catch {
    root.innerHTML = `<p class="muted" style="padding:16px">Transaction not found. <a href="/" id="back-home">← Back home</a></p>`;
    root.querySelector<HTMLAnchorElement>('#back-home')!.onclick = (e) => {
      e.preventDefault();
      void navigate('/');
    };
    return;
  }

  renderReadOnly(root, tx);
}

function renderReadOnly(root: HTMLElement, tx: TransactionDetail) {
  root.innerHTML = `
    <header class="page-header">
      <a href="/" class="back-link" id="back">← Home</a>
      <div style="flex:1"></div>
      <button class="action-btn" id="edit">Edit</button>
      <button class="action-btn danger" id="delete">Delete</button>
    </header>
    <main class="detail-view">
      <div class="detail-amount">${tx.amount_original} ${escapeHtml(tx.currency_original)}</div>
      <div class="detail-merchant">${escapeHtml(tx.merchant ?? '(no merchant)')}</div>
      <div class="detail-category">
        ${escapeHtml(tx.category_name ?? '')}${tx.subcategory_name ? ' / ' + escapeHtml(tx.subcategory_name) : ''}
      </div>
      <hr/>
      <p class="detail-meta muted">
        Logged ${new Date(tx.occurred_at).toLocaleString()}<br/>
        Source: ${escapeHtml(tx.source)}<br/>
        AED equivalent: ${(Math.round(tx.amount_aed * 100) / 100).toFixed(2)}
      </p>
      ${tx.description ? `<p class="detail-description">${escapeHtml(tx.description)}</p>` : ''}
    </main>
  `;

  root.querySelector<HTMLAnchorElement>('#back')!.onclick = (e) => {
    e.preventDefault();
    void navigate('/');
  };
  root.querySelector<HTMLButtonElement>('#edit')!.onclick = () => { void renderEditForm(root, tx); };
  root.querySelector<HTMLButtonElement>('#delete')!.onclick = () => { void confirmDelete(tx.id); };
}

async function renderEditForm(root: HTMLElement, tx: TransactionDetail) {
  root.innerHTML = `<p class="muted" style="padding:16px">Loading…</p>`;
  const cats = await listCategories();
  // Top-level categories only; filter by kind matching transaction.type (all for internal_transfer).
  const eligible: CategoryListItem[] = cats.categories.filter((c) => {
    if (c.parentId) return false;
    if (tx.type === 'internal_transfer') return true;
    return c.kind === tx.type;
  });

  root.innerHTML = `
    <header class="page-header">
      <a href="#" class="back-link" id="cancel-link">← Cancel</a>
      <h1>Edit</h1>
    </header>
    <main class="edit-form">
      <label>Amount
        <input id="f-amount" type="number" step="0.01" value="${tx.amount_original}">
      </label>
      <label>Currency
        <select id="f-currency">
          ${['AED','GBP','USD','EUR','CHF','JPY'].map((c) =>
            `<option value="${c}" ${c === tx.currency_original ? 'selected' : ''}>${c}</option>`
          ).join('')}
        </select>
      </label>
      <label>Merchant
        <input id="f-merchant" type="text" value="${escapeAttr(tx.merchant ?? '')}">
      </label>
      <label>Description
        <input id="f-description" type="text" value="${escapeAttr(tx.description ?? '')}">
      </label>
      <label>Category
        <select id="f-category">
          ${eligible.map((c) =>
            `<option value="${c.id}" ${c.id === tx.category_id ? 'selected' : ''}>${escapeHtml(c.name)}</option>`
          ).join('')}
        </select>
      </label>
      <label>Date
        <input id="f-occurred" type="datetime-local" value="${toLocalDateTimeInputValue(tx.occurred_at)}">
      </label>
      <p id="edit-status" class="muted" style="font-size:12px;min-height:16px"></p>
    </main>
    <footer class="edit-footer">
      <button class="secondary" id="cancel-btn">Cancel</button>
      <button id="save-btn">Save</button>
    </footer>
  `;

  const cancel = () => renderReadOnly(root, tx);
  root.querySelector<HTMLAnchorElement>('#cancel-link')!.onclick = (e) => { e.preventDefault(); cancel(); };
  root.querySelector<HTMLButtonElement>('#cancel-btn')!.onclick = cancel;

  root.querySelector<HTMLButtonElement>('#save-btn')!.onclick = async () => {
    const status = root.querySelector<HTMLElement>('#edit-status')!;
    const saveBtn = root.querySelector<HTMLButtonElement>('#save-btn')!;
    const cancelBtn = root.querySelector<HTMLButtonElement>('#cancel-btn')!;
    const cancelLink = root.querySelector<HTMLAnchorElement>('#cancel-link')!;
    saveBtn.disabled = true;
    cancelBtn.disabled = true;
    cancelLink.style.pointerEvents = 'none';
    cancelLink.style.opacity = '0.5';
    status.textContent = 'Saving…';

    const patch: Record<string, unknown> = {
      amount_original: Number((root.querySelector<HTMLInputElement>('#f-amount')!).value),
      currency_original: (root.querySelector<HTMLSelectElement>('#f-currency')!).value,
      merchant: (root.querySelector<HTMLInputElement>('#f-merchant')!).value || null,
      description: (root.querySelector<HTMLInputElement>('#f-description')!).value || null,
      category_id: (root.querySelector<HTMLSelectElement>('#f-category')!).value,
      occurred_at: new Date((root.querySelector<HTMLInputElement>('#f-occurred')!).value).toISOString(),
    };

    try {
      const updated = await patchTransaction(tx.id, patch);
      renderReadOnly(root, updated);
    } catch (err) {
      status.textContent = `Save failed: ${err instanceof Error ? err.message : String(err)}`;
      saveBtn.disabled = false;
      cancelBtn.disabled = false;
      cancelLink.style.pointerEvents = '';
      cancelLink.style.opacity = '';
    }
  };
}

async function confirmDelete(id: string) {
  if (!window.confirm("Delete this transaction? This can't be undone.")) return;
  try {
    await deleteTransaction(id);
    await navigate('/');
  } catch (err) {
    window.alert(`Delete failed: ${err instanceof Error ? err.message : String(err)}`);
  }
}

/**
 * Convert a UTC ISO timestamp to a `datetime-local` input value in the user's
 * local timezone. The input format is `YYYY-MM-DDTHH:mm` with no timezone info.
 *
 * Round-trip with `new Date(value).toISOString()` is correct because the browser
 * parses the local-format input as local time.
 */
function toLocalDateTimeInputValue(utcIso: string): string {
  const d = new Date(utcIso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!));
}
function escapeAttr(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
}
